/**
 * Web Push (PWA notifikace) — odběr na klientu.
 *
 * Tok: vyžádej VAPID veřejný klíč z backendu → požádej o povolení notifikací →
 * pushManager.subscribe → odešli subscription na backend. Funguje jen v PWA/HTTPS
 * a v prohlížečích s Push API (Chrome/Edge/Firefox; iOS pouze po „Přidat na plochu").
 */
import { api } from './api';

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

/** Aktuální stav: existuje aktivní subscription pro toto zařízení? */
export async function getPushState(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

/**
 * Zapne push pro toto zařízení. Vrací true při úspěchu.
 * Vyhodí Error se srozumitelnou zprávou, když to nejde (zamítnuté povolení,
 * nenastavené VAPID klíče na serveru, nepodporovaný prohlížeč).
 */
export async function enablePush(): Promise<boolean> {
  if (!isPushSupported()) {
    throw new Error('Tento prohlížeč push notifikace nepodporuje.');
  }
  const { public_key } = await api.get<{ public_key: string }>('/notifikace/vapid-public-key');
  if (!public_key) {
    throw new Error('Push notifikace nejsou na serveru nakonfigurované (chybí VAPID klíče).');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Povolení notifikací bylo zamítnuto.');
  }

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }

  const json = sub.toJSON();
  await api.post('/notifikace/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });
  return true;
}

/** Vypne push pro toto zařízení (odhlásí z prohlížeče i z backendu). */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* i tak zkusíme smazat na backendu */
  }
  await api.post('/notifikace/push/unsubscribe', { endpoint }).catch(() => {});
}
